import { useEffect, useRef } from 'react';
import { useAppSelector } from 'app/hooks';
import type { IAhuBoardSection, IAhuBoardTunnel, IAhuBoardUnit, ICmsAhuDoc } from 'models';
import { writeLastSelectedSections } from '../../ahuBoardStorage';
import SectionForm from './SectionForm';
import BulkSectionsPanel from './BulkSectionsPanel';

/**
 * Phase 6e1 — sections tab orchestrator (§7.4, view/tabs/tab.sections.js):
 * SectionForm for the CURRENT section (`.active`), the multi-select CSS mode
 * (`.multi-sections-selected` on the 5 chrome roots) + bulk panel (≥2
 * selected), and the `last_selected_sections` localStorage writer (second
 * writer besides the canvas visibilitychange — §1.12 / parity 107).
 *
 * PERF (6e1 verify 6e1-3): the legacy view keeps one ViewTabSection per
 * section in the DOM and hides the inactive ones via CSS — cheap for jQuery,
 * but mounting 15 SectionForms × ~6 PaginatedSelects froze React on Ctrl
 * multi-select (sync-render storm). Only the VISIBLE form is mounted here:
 * single-select → the current section's form; multi-select → the bulk panel
 * replaces the forms entirely (that is all the legacy CSS ever showed).
 * Per-section subtab expansion lives in the slice so it survives remounts.
 */

const MULTI_ROOTS = [
  '#ahu-board-content',
  '#forms_sections',
  '#forms-main-container',
  '#canvas_toolbar',
  '#components_section',
];

interface TabSectionsProps {
  ahu: IAhuBoardUnit;
  tunnels: IAhuBoardTunnel[];
  sections: IAhuBoardSection[];
  cms: ICmsAhuDoc | undefined;
  mfgId: number;
}

const TabSections = ({ ahu, tunnels, sections, cms, mfgId }: TabSectionsProps) => {
  const currentSectionId = useAppSelector((s) => s.ahuBoard.selection.currentSectionId);
  const selectedIds = useAppSelector((s) => s.ahuBoard.selection.selectedSectionIds);
  const multi = selectedIds.length > 1;
  // Legacy's writer (check_multiply_list) is event-bound only — it never fires
  // at boot, so the persisted `last_selected_sections` survives until the
  // canvas decideBootSelection reads it. Clearing on an empty selection is
  // therefore only valid after a selection existed this session.
  const hadSelection = useRef(false);

  // `.multi-sections-selected` chrome flip + localStorage writer (§7.4).
  useEffect(() => {
    MULTI_ROOTS.forEach((sel) => {
      document.querySelector(sel)?.classList.toggle('multi-sections-selected', multi);
    });
    if (selectedIds.length) {
      hadSelection.current = true;
      writeLastSelectedSections(
        selectedIds
          .map((id) => sections.find((s) => s.id === id))
          .filter((s): s is IAhuBoardSection => !!s)
          .map((s) => ({ ahu_id: ahu.id, section_id: s.id, tunnel_id: s.tunnel_id }))
      );
    } else if (hadSelection.current) {
      writeLastSelectedSections([]);
    }
    return () => {
      MULTI_ROOTS.forEach((sel) =>
        document.querySelector(sel)?.classList.remove('multi-sections-selected')
      );
    };
  }, [multi, selectedIds, sections, ahu.id]);

  const selectedSections = selectedIds
    .map((id) => sections.find((s) => s.id === id))
    .filter((s): s is IAhuBoardSection => !!s);

  // Mount ONLY what is visible (see PERF note above): the bulk panel in
  // multi mode, otherwise the current section's form.
  const currentSection = multi
    ? undefined
    : sections.find((s) => s.id === currentSectionId);

  return (
    <>
      <div id="multiply-sections" className="section">
        {multi && (
          <BulkSectionsPanel
            ahu={ahu}
            tunnels={tunnels}
            selectedSections={selectedSections}
            cms={cms}
            mfgId={mfgId}
          />
        )}
      </div>
      {currentSection && (
        <SectionForm
          key={currentSection.id}
          ahu={ahu}
          section={currentSection}
          tunnel={tunnels.find((t) => t.id === currentSection.tunnel_id)}
          cms={cms}
          mfgId={mfgId}
          active
        />
      )}
    </>
  );
};

export default TabSections;
